import React, { useEffect, useState } from 'react';
import CollapseHeader from '../../../core/common/collapse-header/collapse-header';
import Table from '../../../core/common/dataTable/index';
import {
  createOrgRole,
  fetchOrgRole,
  fetchOrgRoles,
  updateOrgRole,
  updateOrgRolePrivileges,
} from '../../../core/data/redux/service/OrgRoleService';
import { formatError, formatSuccess } from '../../../core/data/redux/service/AuthService';
import { AxiosResponse } from 'axios';

const OrgRoles = () => {
  const [roles, setRoles] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [privileges, setPrivileges] = useState<any[]>([]);
  const [landingScreen, setLandingScreen] = useState('');
  const [roleActive, setRoleActive] = useState(true);
  const [newRole, setNewRole] = useState({ role: '', status: true });

  const loadRoles = () => {
    fetchOrgRoles(0, 100)
      .then((res: AxiosResponse) => setRoles(res.data.content || []))
      .catch(err => formatError(err));
  };

  useEffect(() => { loadRoles(); }, []);

  const loadRoleDetails = (roleName: string) => {
    setSelectedRole(roleName);
    fetchOrgRole(roleName).then((res: AxiosResponse) => {
      const catalog = res.data?.privileges || [];
      const savedLanding = res.data?.landingScreen || '';
      setPrivileges(catalog);
      const validLanding = catalog.some((p: any) => p.key === savedLanding && p.status)
        ? savedLanding
        : '';
      setLandingScreen(validLanding);
      setRoleActive(res.data?.status !== false);
    }).catch(err => formatError(err));
  };

  const landingOptions = privileges.filter(p => p.status);

  const saveLandingScreen = () => {
    if (!selectedRole || !landingScreen) return;
    updateOrgRole({ role: selectedRole, landingScreen, status: roleActive })
      .then(() => {
        formatSuccess('Landing screen updated');
        loadRoles();
      })
      .catch(err => formatError(err));
  };

  const togglePrivilege = (key: string) => {
    setPrivileges(prev => prev.map(p => {
      if (p.key !== key) return p;
      const nextStatus = !p.status;
      if (!nextStatus && landingScreen === key) {
        setLandingScreen('');
      }
      return { ...p, status: nextStatus };
    }));
  };

  const savePrivileges = () => {
    updateOrgRolePrivileges({
      role: selectedRole,
      privileges: privileges.map(p => ({ key: p.key, status: !!p.status })),
    })
      .then(() => formatSuccess('Privileges updated'))
      .catch(err => formatError(err));
  };

  const handleCreateRole = (e: React.FormEvent) => {
    e.preventDefault();
    createOrgRole({ role: newRole.role, status: newRole.status })
      .then(() => {
      formatSuccess('Role created');
      setNewRole({ role: '', status: true });
      loadRoles();
    })
      .catch(err => formatError(err));
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2"><h2 className="mb-1">Organisation Roles & Privileges</h2></div>
          <div className="head-icons ms-2"><CollapseHeader /></div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <form className="row g-3 align-items-center" onSubmit={handleCreateRole}>
              <div className="col-md-8">
                <input className="form-control" placeholder="Role name" value={newRole.role}
                  onChange={e => setNewRole({ ...newRole, role: e.target.value.toUpperCase() })} required />
              </div>
              <div className="col-md-4">
                <button type="submit" className="btn btn-primary w-100">Create Role</button>
              </div>
            </form>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4">
            <div className="card">
              <div className="card-header"><h5>Roles</h5></div>
              <div className="list-group list-group-flush">
                {roles.map((r: any) => (
                  <button key={r.role} type="button"
                    className={`list-group-item list-group-item-action ${selectedRole === r.role ? 'active' : ''}`}
                    onClick={() => loadRoleDetails(r.role)}>
                    {r.role}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="col-md-8">
            

            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{selectedRole ? `Privileges — ${selectedRole}` : 'Select a role'}</h5>
                {selectedRole && (
                  <button type="button" className="btn btn-sm btn-primary" onClick={savePrivileges}>Save</button>
                )}
              </div>
              <div className="card-body">
                {privileges.length === 0 ? (
                  <p className="text-muted mb-0">Select a role to manage screen privileges.</p>
                ) : (
                  <div className="row">
                    {privileges.map(p => (
                      <div key={p.key} className="col-md-6 mb-2">
                        <label className="d-flex align-items-center gap-2">
                          <input type="checkbox" checked={!!p.status} onChange={() => togglePrivilege(p.key)} />
                          <span>{p.label || p.key}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="card mb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{selectedRole ? `Role Settings — ${selectedRole}` : 'Select a role'}</h5>
                {selectedRole && (
                  <button type="button" className="btn btn-sm btn-primary" onClick={saveLandingScreen} disabled={!landingScreen}>
                    Save Landing Screen
                  </button>
                )}
              </div>
              <div className="card-body">
                {!selectedRole ? (
                  <p className="text-muted mb-0">Select a role to configure its landing screen.</p>
                ) : (
                  <div className="row g-3 align-items-end">
                    <div className="col-md-6">
                      <label className="form-label">Landing screen</label>
                      <select
                        className="form-select"
                        value={landingScreen}
                        onChange={e => setLandingScreen(e.target.value)}
                        disabled={landingOptions.length === 0}
                      >
                        <option value="">Select landing screen</option>
                        {landingOptions.map((p: any) => (
                          <option key={p.key} value={p.key}>{p.label || p.key}</option>
                        ))}
                      </select>
                      {landingOptions.length === 0 && (
                        <small className="text-muted">Enable at least one screen privilege below first.</small>
                      )}
                    </div>
                    <div className="col-md-3 form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="role-active"
                        checked={roleActive}
                        onChange={e => setRoleActive(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="role-active">Active</label>
                    </div>
                    <div className="col-md-3">
                      <small className="text-muted">
                        Current: <strong>{landingScreen || 'not set'}</strong>
                      </small>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgRoles;
